import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Star, MapPin, Phone, Mail, Instagram, Award, Gift, Percent, Users, ChevronDown } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve media URLs - converts old preview URLs to relative paths
const resolveMediaUrl = (url) => {
  if (!url) return url;
  const match = url.match(/\/api\/files\/.+/);
  if (match) return `${BACKEND_URL}${match[0]}`;
  return url;
};

// Header Component
const Header = ({ settings }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/70 border-b border-[#0A192F]/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt={settings.hotel_name} className="h-14 sm:h-20 object-contain" data-testid="hotel-logo" />
          ) : null}
          <span className="font-heading text-lg sm:text-2xl font-semibold text-[#0A192F] hidden sm:inline">{settings.hotel_name}</span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#reviews" className="text-[#0A192F]/70 hover:text-[#FF6B6B] transition-colors" data-testid="nav-reviews">Reviews</a>
          <a href="#specials" className="text-[#0A192F]/70 hover:text-[#FF6B6B] transition-colors" data-testid="nav-specials">Specials</a>
          <a href="#instagram" className="text-[#0A192F]/70 hover:text-[#FF6B6B] transition-colors" data-testid="nav-instagram">Instagram</a>
          <a href="/admin" className="text-[#0A192F]/70 hover:text-[#FF6B6B] transition-colors" data-testid="nav-admin">Admin</a>
        </nav>
        <div className="flex items-center gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="btn-primary text-xs sm:text-sm px-4 sm:px-8 py-2 sm:py-4" data-testid="register-btn">
                <span className="hidden sm:inline">Register & Review</span>
                <span className="sm:hidden">Review</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl mx-4">
              <RegistrationForm settings={settings} />
            </DialogContent>
          </Dialog>
          {/* Mobile menu button */}
          <button className="md:hidden p-2 text-[#0A192F]" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d={menuOpen ? "M6 6l12 12M6 18L18 6" : "M3 12h18M3 6h18M3 18h18"} /></svg>
          </button>
        </div>
      </div>
      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur border-t border-[#0A192F]/10 px-4 py-4 space-y-3">
          <a href="#reviews" className="block text-[#0A192F]/70 hover:text-[#FF6B6B] py-2" onClick={() => setMenuOpen(false)}>Reviews</a>
          <a href="#specials" className="block text-[#0A192F]/70 hover:text-[#FF6B6B] py-2" onClick={() => setMenuOpen(false)}>Specials</a>
          <a href="#instagram" className="block text-[#0A192F]/70 hover:text-[#FF6B6B] py-2" onClick={() => setMenuOpen(false)}>Instagram</a>
          <a href="/admin" className="block text-[#0A192F]/70 hover:text-[#FF6B6B] py-2" onClick={() => setMenuOpen(false)}>Admin</a>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = ({ settings }) => (
  <section className="relative min-h-[100svh] flex items-center" data-testid="hero-section">
    {settings.hero_image_url && settings.hero_image_url.match(/\.(mp4|mov|webm)(\?|$)/i) ? (
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={settings.hero_image_url}
        autoPlay muted loop playsInline
      />
    ) : (
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${settings.hero_image_url})`,
        }}
      />
    )}
    <div className="absolute inset-0 hero-overlay" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 py-24 sm:py-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-2xl"
      >
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B6B] mb-3 sm:mb-4 block">
          Daytona Beach, Florida
        </span>
        <h1 className="font-heading text-3xl sm:text-5xl lg:text-7xl text-white leading-none mb-4 sm:mb-6">
          {settings.hotel_name}
        </h1>
        <div className="inline-block bg-black/50 backdrop-blur-sm border border-[#D4AF37]/60 rounded-lg px-5 py-3 sm:px-6 sm:py-4 mb-4 sm:mb-5">
          <p className="text-sm sm:text-xl md:text-2xl font-heading font-bold text-[#D4AF37] leading-snug whitespace-nowrap">
            Score Your Getaway: <span className="text-white">"Register to Win"</span> <span className="uppercase text-[#FF6B6B]">FREE</span> <span className="text-white">Night Stay</span>
          </p>
        </div>
        <p className="text-sm sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 font-body leading-relaxed">
          {settings.tagline}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="btn-primary text-sm" data-testid="hero-register-btn">Leave a Review</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl mx-4">
              <RegistrationForm settings={settings} />
            </DialogContent>
          </Dialog>
          <a href="#specials">
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0A192F] px-6 sm:px-8 py-3 sm:py-4 uppercase tracking-widest text-xs sm:text-sm w-full sm:w-auto" data-testid="hero-specials-btn">
              View Specials
            </Button>
          </a>
        </div>
      </motion.div>
    </div>
    <a href="#winners" className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 text-white animate-bounce">
      <ChevronDown size={28} />
    </a>
  </section>
);

// Winners Marquee
const WinnersMarquee = ({ winners }) => (
  <section id="winners" className="marquee-wrapper py-6" data-testid="winners-marquee">
    <Marquee speed={40} gradient={false}>
      {winners.length > 0 ? (
        winners.map((winner, idx) => (
          <span key={idx} className="marquee-text mx-12">
            <Award className="inline-block mr-2 text-[#D4AF37]" size={24} />
            Congratulations {winner.guest_name}! — {winner.prize}
            <span className="mx-8 text-[#FF6B6B]">*</span>
          </span>
        ))
      ) : (
        <span className="marquee-text mx-12">
          <Gift className="inline-block mr-2 text-[#D4AF37]" size={24} />
          Register today for your chance to win our weekly gift certificate!
          <span className="mx-8 text-[#FF6B6B]">*</span>
        </span>
      )}
    </Marquee>
  </section>
);

// Star Rating Component
const StarRating = ({ rating, onRate, interactive = false }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-1" style={{ position: "relative", zIndex: 50 }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onRate && onRate(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={`p-0 bg-transparent border-none ${interactive ? "cursor-pointer star-interactive" : ""}`}
          data-testid={`star-${star}`}
          style={{ lineHeight: 0 }}
        >
          <Star
            size={interactive ? 32 : 20}
            className={`pointer-events-none ${
              (hover || rating) >= star ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#0A192F]/20"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Registration & Review Form
const RegistrationForm = ({ settings = {} }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    room_number: "",
    rating: 0,
    review: "",
  });
  const [loading, setLoading] = useState(false);
  const [guestId, setGuestId] = useState(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in name and email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/guests/register`, {
        name: formData.name,
        email: formData.email,
        room_number: formData.room_number,
      });
      const data = res.data;
      setGuestId(data.guest.id);
      setFormData((prev) => ({ ...prev, name: data.guest.name, email: data.guest.email }));
      
      if (data.already_registered) {
        setAlreadyRegistered(true);
        toast.info("You've already been registered! You can still leave a review.");
      } else {
        toast.success("Registration successful! You're entered in our weekly drawing.");
      }
      setStep(2);
    } catch (err) {
      toast.error("Registration failed. Please try again.");
    }
    setLoading(false);
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!formData.rating) {
      toast.error("Please select a rating");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        guest_id: guestId,
        guest_name: formData.name,
        guest_email: formData.email,
        rating: formData.rating,
        text: formData.review,
      });
      toast.success("Thank you for your review!");
      setStep(3);
    } catch (err) {
      toast.error("Failed to submit review. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="p-2 sm:p-4">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl sm:text-3xl text-[#0A192F]">
          {step === 1 && "Register as a Guest"}
          {step === 2 && (alreadyRegistered ? "Welcome Back!" : "Share Your Experience")}
          {step === 3 && "Thank You!"}
        </DialogTitle>
        <DialogDescription className="text-[#4A5568] text-sm">
          {step === 1 && "Join our community for weekly gift certificate drawings"}
          {step === 2 && (alreadyRegistered ? "You're already registered. Leave a new review below!" : "Your feedback helps us serve you better")}
          {step === 3 && "Your review has been submitted for approval"}
        </DialogDescription>
      </DialogHeader>

      {step === 1 && (
        <form onSubmit={handleRegister} className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/60 mb-2 block">
              Full Name
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="form-input w-full"
              placeholder="John Smith"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/60 mb-2 block">
              Email Address
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="form-input w-full"
              placeholder="john@example.com"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/60 mb-2 block">
              Room Number <span className="text-[#4A5568]/50 normal-case tracking-normal">(optional)</span>
            </label>
            <Input
              type="text"
              value={formData.room_number}
              onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
              className="form-input w-full"
              placeholder="e.g., 204"
              data-testid="register-room-input"
            />
          </div>
          <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit-btn">
            {loading ? "Registering..." : "Register & Continue"}
          </Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleReview} className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
          {alreadyRegistered && (
            <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="already-registered-msg">
              You've already been registered with this email. You can still leave a new review!
            </div>
          )}
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/60 mb-4 block">
              Your Rating
            </label>
            <StarRating
              rating={formData.rating}
              onRate={(r) => setFormData({ ...formData, rating: r })}
              interactive
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/60 mb-2 block">
              Your Review
            </label>
            <Textarea
              value={formData.review}
              onChange={(e) => setFormData({ ...formData, review: e.target.value })}
              className="form-input w-full min-h-[100px] sm:min-h-[120px] resize-none"
              placeholder="Tell us about your stay..."
              data-testid="review-text-input"
            />
          </div>
          <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="review-submit-btn">
            {loading ? "Submitting..." : "Submit Review"}
          </Button>
        </form>
      )}

      {step === 3 && (
        <div className="text-center py-8">
          <div className="w-20 h-20 mx-auto mb-6 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
            <Award className="text-[#D4AF37]" size={40} />
          </div>
          <p className="text-lg text-[#4A5568] mb-4">
            Your review is pending approval and will appear on our site soon.
          </p>
          <p className="text-[#FF6B6B] font-medium mb-6">
            You're now entered in our weekly gift certificate drawing!
          </p>
          {formData.rating === 5 && settings.google_review_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="relative overflow-hidden bg-gradient-to-br from-[#0A192F] to-[#1a2d4a] rounded-xl p-6 mt-6 shadow-xl border border-[#D4AF37]/40"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#FF6B6B]/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex justify-center gap-1 mb-3">
                  {[1,2,3,4,5].map((s) => (
                    <Star key={s} size={20} className="fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="font-heading text-xl text-white mb-1">
                  You Made Our Day!
                </p>
                <p className="text-sm text-white/70 mb-5">
                  Help other travelers discover us — share your 5-star experience on Google!
                </p>
                <a
                  href={settings.google_review_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-review-link"
                >
                  <Button className="bg-[#D4AF37] hover:bg-[#c9a52e] text-[#0A192F] font-bold px-8 py-3 rounded-full uppercase tracking-wider text-sm shadow-lg transition-all hover:scale-105">
                    <Star size={16} className="mr-2 fill-[#0A192F]" /> Leave a Google Review
                  </Button>
                </a>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};

// Reviews Section
const ReviewsSection = ({ reviews }) => (
  <section id="reviews" className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white" data-testid="reviews-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-left mb-6 sm:mb-8">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B6B] mb-2 block">
          Guest Experiences
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0A192F]">What Our Guests Say</h2>
      </div>
      {reviews.length > 0 ? (
        <div className="border border-[#0A192F]/10 bg-[#FDFCF9] p-6 max-h-[480px] overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "#D4AF37 #F7F5F0" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, idx) => (
              <Card key={review.id} className="card-flamingo h-full" data-testid={`review-card-${idx}`}>
                <CardContent className="p-8">
                  <StarRating rating={review.rating} />
                  <p className="mt-4 text-[#4A5568] leading-relaxed">{review.text}</p>
                  <div className="mt-6 pt-4 border-t border-[#0A192F]/10">
                    <p className="font-medium text-[#0A192F]">{review.guest_name}</p>
                    <p className="text-sm text-[#4A5568]">Verified Guest</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="mx-auto text-[#0A192F]/20 mb-4" size={48} />
          <p className="text-[#4A5568]">Be the first to share your experience!</p>
        </div>
      )}
    </div>
  </section>
);

// Weekly Specials Section
const SpecialsSection = ({ specials, settings }) => (
  <section
    id="specials"
    className="section-padding relative overflow-hidden"
    data-testid="specials-section"
  >
    {settings.specials_bg_image_url && settings.specials_bg_image_url.match(/\.(mp4|mov|webm)(\?|$)/i) ? (
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={settings.specials_bg_image_url}
        autoPlay muted loop playsInline
      />
    ) : (
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${settings.specials_bg_image_url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
    )}
    <div className="absolute inset-0 bg-[#0A192F]/80" />
    <div className="relative z-10 max-w-7xl mx-auto">
      <div className="text-left mb-12">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B6B] mb-2 block">
          {specials.some(s => s.listing_type === "regular") && !specials.some(s => s.listing_type !== "regular") ? "What's Happening" : "Limited Time Offers"}
        </span>
        <h2 className="font-heading text-4xl sm:text-5xl text-white">
          {specials.some(s => s.listing_type === "regular") && !specials.some(s => s.listing_type !== "regular") ? "Events" : specials.some(s => s.listing_type === "regular") ? "Specials & Events" : "Weekly Specials"}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {specials.length > 0 ? (
          specials.map((special, idx) => (
            <motion.div
              key={special.id}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white/10 backdrop-blur border-white/20 h-full overflow-hidden" data-testid={`special-card-${idx}`}>
                {special.image_url && (
                  <div className={`${special.listing_type === "regular" ? "h-64" : "h-40"} overflow-hidden`}>
                    <img src={special.image_url} alt={special.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <CardContent className="p-8">
                  {special.listing_type === "regular" ? null : (
                    <div className="badge-gold inline-block mb-4">
                      <Percent className="inline mr-1" size={14} />
                      {special.discount_percent}% OFF
                    </div>
                  )}
                  <h3 className="font-heading text-2xl text-white mb-2">{special.title}</h3>
                  <p className="text-white/70 mb-4">{special.description}</p>
                  {special.listing_type !== "regular" && (
                    <div className="text-sm text-white/60">
                      <p>Room Type: {special.room_type}</p>
                      <p>Valid Until: {special.valid_until}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <Percent className="mx-auto text-white/30 mb-4" size={48} />
            <p className="text-white/60">New specials coming soon!</p>
          </div>
        )}
      </div>
    </div>
  </section>
);

// Instagram Feed Section
const InstagramSection = ({ feed, trophyWinners, settings }) => (
  <section id="instagram" className="section-padding bg-[#F7F5F0]" data-testid="instagram-section">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-12 gap-4">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B6B] mb-2 block">
            Follow Our Journey
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl text-[#0A192F]">
            <Instagram className="inline mr-3" size={40} />
            {settings.instagram_handle}
          </h2>
        </div>
        {settings.instagram_url && (
          <a
            href={settings.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-handle-link"
          >
            <Button className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white font-bold px-6 py-3 rounded-full uppercase tracking-wider text-sm shadow-lg transition-all hover:scale-105">
              <Instagram size={18} className="mr-2" /> Follow Us on Instagram
            </Button>
          </a>
        )}
      </div>
      <div className="border border-[#0A192F]/10 bg-[#FDFCF9] p-6 max-h-[480px] overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "#D4AF37 #F7F5F0" }}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {feed.map((post, idx) => (
            <div
              key={post.id}
              className="relative group"
              data-testid={`instagram-post-${idx}`}
            >
              <div className="aspect-square overflow-hidden bg-[#E8E4D9]">
                {post.media_type === "video" ? (
                  <video
                    src={post.image_url}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    muted loop playsInline autoPlay
                  />
                ) : (
                  <img
                    src={post.image_url}
                    alt={post.caption}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
              </div>
              {post.is_winner && (
                <div className="absolute top-2 right-2 bg-[#D4AF37] text-white px-2 py-0.5 text-xs font-bold uppercase tracking-wider">
                  <Award className="inline mr-1" size={10} /> Winner
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-xs line-clamp-2">{post.caption}</p>
                <p className="text-white/70 text-xs mt-1">{post.likes} likes</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Trophy Winners */}
      {trophyWinners.length > 0 && (
        <div className="mt-20">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-2 block">
              Hall of Fame
            </span>
            <h3 className="font-heading text-4xl sm:text-5xl text-[#0A192F]">
              Trophy Winners
            </h3>
            <p className="text-[#4A5568] mt-2 max-w-xl mx-auto">Congratulations to our lucky guests who won complimentary stays at Flamingo Inn</p>
          </div>
          <div className="relative border border-[#D4AF37]/20 bg-white p-6 max-h-[480px] overflow-y-auto" data-testid="trophy-scroll-box" style={{ scrollbarWidth: "thin", scrollbarColor: "#D4AF37 #F7F5F0" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {trophyWinners.map((winner, idx) => {
                const fmtDate = (d) => {
                  if (!d) return "";
                  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
                  catch { return d; }
                };
                return (
                  <div key={winner.id} className="border border-[#D4AF37]/15 bg-[#FDFCF9] p-5 text-center" data-testid={`trophy-winner-${idx}`}>
                    <div className="w-12 h-12 mx-auto mb-3 bg-[#D4AF37]/10 rounded-full flex items-center justify-center border border-[#D4AF37]/30">
                      <Award className="text-[#D4AF37]" size={22} />
                    </div>
                    <p className="font-heading text-xl font-semibold text-[#0A192F]">{winner.guest_name}</p>
                    <p className="text-[#D4AF37] font-heading text-base font-medium mt-1">{winner.prize}</p>
                    {(winner.check_in_date && winner.check_out_date) && (
                      <p className="text-xs text-[#4A5568] bg-[#F7F5F0] inline-block px-3 py-1 mt-2">
                        {fmtDate(winner.check_in_date)} — {fmtDate(winner.check_out_date)}
                      </p>
                    )}
                    {winner.barcode && (
                      <p className="text-xs text-[#4A5568]/60 font-mono mt-2">{winner.barcode}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  </section>
);

// Footer
const Footer = ({ settings }) => (
  <footer className="bg-[#0A192F] text-white section-padding" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <h3 className="font-heading text-3xl mb-4">{settings.hotel_name}</h3>
          <p className="text-white/60 leading-relaxed">
            Your oceanfront paradise in beautiful Daytona Beach, Florida.
          </p>
        </div>
        <div>
          <h4 className="font-heading text-xl mb-4">Contact</h4>
          <div className="space-y-3 text-white/60">
            <p className="flex items-center gap-2">
              <MapPin size={18} /> {settings.address}
            </p>
            <p className="flex items-center gap-2">
              <Phone size={18} /> {settings.phone}
            </p>
            <p className="flex items-center gap-2">
              <Mail size={18} /> {settings.email}
            </p>
          </div>
        </div>
        <div>
          <h4 className="font-heading text-xl mb-4">Follow Us</h4>
          <a
            href={settings.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-white/60 hover:text-[#FF6B6B] transition-colors"
          >
            <Instagram size={18} /> {settings.instagram_handle}
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/40 text-sm">
        <p>&copy; 2024 {settings.hotel_name} Daytona Beach. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Default settings fallback
const DEFAULT_SETTINGS = {
  hotel_name: "Flamingo Inn",
  tagline: "Experience the perfect blend of vintage Florida charm and modern comfort. Your oceanfront paradise awaits.",
  logo_url: "",
  hero_image_url: "https://static.prod-images.emergentagent.com/jobs/5a095721-7c3b-4f3b-a124-47fa92eaea25/images/7301d467b1c330e06c1f8c13872200b95b7970a50773592741f190fd9872fc6e.png",
  specials_bg_image_url: "https://images.unsplash.com/photo-1587870306141-4f19861e6c73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxmbG9yaWRhJTIwYmVhY2glMjByZXNvcnQlMjBwb29sJTIwcGFsbSUyMHRyZWVzfGVufDB8fHx8MTc3NTkxMDM2NXww&ixlib=rb-4.1.0&q=85",
  address: "123 Ocean Drive, Daytona Beach, FL",
  phone: "(386) 555-0123",
  email: "info@flamingoinn.com",
  instagram_handle: "@FlamingoInnDaytona",
  instagram_url: "https://www.instagram.com/flamingoinn_daytona",
  google_review_url: "https://www.google.com/maps/place/Flamingo+Inn,+2011+S+Atlantic+Ave,+Daytona+Beach+Shores,+FL+32118",
};

// Main HomePage Component
const HomePage = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [reviews, setReviews] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [giftWinners, setGiftWinners] = useState([]);
  const [trophyWinners, setTrophyWinners] = useState([]);
  const [instagramFeed, setInstagramFeed] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, reviewsRes, specialsRes, giftRes, trophyRes, instaRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/reviews?approved_only=true`),
          axios.get(`${API}/specials?active_only=true`),
          axios.get(`${API}/gift-winners`),
          axios.get(`${API}/trophy-winners`),
          axios.get(`${API}/instagram-feed`),
        ]);
        const settingsData = { ...DEFAULT_SETTINGS, ...settingsRes.data };
        // Resolve image URLs for current domain
        ['logo_url', 'hero_image_url', 'specials_bg_image_url'].forEach(k => {
          if (settingsData[k]) settingsData[k] = resolveMediaUrl(settingsData[k]);
        });
        setSettings(settingsData);
        setReviews(reviewsRes.data);
        setSpecials(specialsRes.data.map(s => ({ ...s, image_url: resolveMediaUrl(s.image_url) })));
        setGiftWinners(giftRes.data);
        setTrophyWinners(trophyRes.data);
        setInstagramFeed(instaRes.data.map(p => ({ ...p, image_url: resolveMediaUrl(p.image_url) })));
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header settings={settings} />
      <HeroSection settings={settings} />
      <WinnersMarquee winners={giftWinners} />
      <ReviewsSection reviews={reviews} />
      <SpecialsSection specials={specials} settings={settings} />
      <InstagramSection feed={instagramFeed} trophyWinners={trophyWinners} settings={settings} />
      <Footer settings={settings} />
    </div>
  );
};

export default HomePage;
